import LineChartWidget from './LineChartWidget';
import RoomTableWidget from './RoomTableWidget';
import DonutsWidget from './DonutsWidget';
import KpiBarWidget from './KpiBarWidget';
import { getOverviewSums } from '../services/performanceService';
import {
    mdiChartDonut,
    mdiChartLine,
    mdiGauge,
    mdiTable
} from '@mdi/js';

/**
 * Registry of all widget types available on the Overview dashboard grid.
 * Each entry maps a `widgetType` (persisted in the layout JSON) to:
 * - component: the Vue component rendered inside WidgetHost
 * - defaultW/defaultH, minW/minH: grid units (grid is GRID_COL_NUM columns wide)
 * - titleKey: i18n key for the widget title shown in edit mode / add panel
 * - configSchema: fields rendered in the per-widget settings popover
 * - defaultConfig: config used when the widget is added from the add panel
 */
export const GRID_COL_NUM = 16;
export const GRID_ROW_COUNT = 6;
/** Set to true when the Overview customize UI is ready to ship. */
export const DASHBOARD_CUSTOMIZE_ENABLED = true;
/** Fallback row height in px; Overview recomputes it from the container height. */
export const GRID_ROW_HEIGHT = 100;

const metricOptions = [
    { value: 'avg_score', labelKey: 'avg_score' },
    { value: 'complaints', labelKey: 'complaints' },
    { value: 'response_time_SLA', labelKey: 'response_time_SLA' },
    { value: 'time_saved', labelKey: 'time_saved' }
];

const timeRangeOptions = [
    { value: 'lastMonth', labelKey: 'last_month' },
    { value: 'lastYear', labelKey: 'last_year' }
];

/** Widget categories for the edit-mode add panel. */
export const WIDGET_CATEGORIES = [
    { key: 'charts', labelKey: 'dashboard_category_charts', icon: mdiChartLine },
    { key: 'tables', labelKey: 'dashboard_category_tables', icon: mdiTable },
    { key: 'kpis', labelKey: 'dashboard_category_kpis', icon: mdiGauge }
];

const widgetRegistry = {
    'line-chart': {
        component: LineChartWidget,
        defaultW: 3,
        defaultH: 2,
        minW: 2,
        minH: 2,
        category: 'charts',
        icon: mdiChartLine,
        titleKey: 'dashboard_widget_line_chart',
        descriptionKey: 'dashboard_widget_line_chart_desc',
        defaultConfig: { metric: 'avg_score', timeRange: 'lastMonth' },
        configSchema: [
            { key: 'metric', labelKey: 'dashboard_config_metric', options: metricOptions },
            { key: 'timeRange', labelKey: 'dashboard_config_time_range', options: timeRangeOptions }
        ]
    },
    'room-table': {
        component: RoomTableWidget,
        defaultW: 4,
        defaultH: 6,
        minW: 3,
        minH: 3,
        category: 'tables',
        icon: mdiTable,
        titleKey: 'dashboard_widget_room_table',
        descriptionKey: 'dashboard_widget_room_table_desc',
        defaultConfig: { displayMode: 'table' },
        configSchema: [
            {
                key: 'displayMode',
                labelKey: 'dashboard_config_view_mode',
                options: [
                    { value: 'table', labelKey: 'table' },
                    { value: 'cards', labelKey: 'cards' }
                ]
            }
        ]
    },
    donuts: {
        component: DonutsWidget,
        defaultW: 4,
        defaultH: 2,
        minW: 3,
        minH: 2,
        category: 'charts',
        icon: mdiChartDonut,
        titleKey: 'dashboard_widget_donuts',
        descriptionKey: 'dashboard_widget_donuts_desc',
        defaultConfig: { donut: 'general' },
        configSchema: [
            {
                key: 'donut',
                labelKey: 'dashboard_config_donut',
                options: [
                    { value: 'general', labelKey: 'general' },
                    { value: 'cleaning', labelKey: 'cleaning' },
                    { value: 'maintenance', labelKey: 'maintenance' }
                ]
            }
        ]
    },
    'kpi-bar': {
        component: KpiBarWidget,
        defaultW: 3,
        defaultH: 1,
        minW: 2,
        minH: 1,
        category: 'kpis',
        icon: mdiGauge,
        titleKey: 'dashboard_widget_kpi_bar',
        descriptionKey: 'dashboard_widget_kpi_bar_desc',
        defaultConfig: { kpi: 'avg_score' },
        configSchema: [
            {
                key: 'kpi',
                labelKey: 'dashboard_config_kpi',
                options: [
                    { value: 'avg_score', labelKey: 'avg_score' },
                    { value: 'complaints', labelKey: 'complaints' },
                    { value: 'visits', labelKey: 'visits' },
                    { value: 'response_time_SLA', labelKey: 'response_time_SLA' },
                    { value: 'time_saved', labelKey: 'time_saved' },
                    { value: 'exposures', labelKey: 'exposures' }
                ]
            }
        ]
    }
};

export function getWidgetRegistry() {
    return widgetRegistry;
}

/**
 * Widget types grouped by category for the edit-mode add panel.
 * @returns {Array<{ key: string; labelKey: string; icon: string; widgets: Array<{ widgetType: string; definition: Object }> }>}
 */
export function getWidgetAddPanelGroups() {
    return WIDGET_CATEGORIES.map((category) => ({
        ...category,
        widgets: Object.entries(widgetRegistry)
            .filter(([, definition]) => definition.category === category.key)
            .map(([widgetType, definition]) => ({ widgetType, definition }))
    })).filter((group) => group.widgets.length > 0);
}

export function getWidgetDefinition(widgetType) {
    return widgetRegistry[widgetType] || null;
}

/**
 * @typedef {Object} WidgetSiteContext
 * @property {boolean} [isTimeSave]
 * @property {boolean} [exposures]
 */

/**
 * Build site context from Vuex getters (or any object with currentFilteringSite / currentSiteInfo).
 * @param {{ currentFilteringSite?: Object; currentSiteInfo?: Object }} source
 * @returns {WidgetSiteContext}
 */
export function buildWidgetSiteContext(source = {}) {
    const currentFilteringSite = source.currentFilteringSite;
    const currentSiteInfo = source.currentSiteInfo;
    return {
        isTimeSave: !!(currentFilteringSite && currentFilteringSite.isTimeSave),
        exposures: !!(currentSiteInfo && currentSiteInfo.exposures)
    };
}

/** @param {WidgetSiteContext} siteContext */
export function isTimeSaveAvailable(siteContext) {
    return !!(siteContext && siteContext.isTimeSave);
}

/** @param {WidgetSiteContext} siteContext */
export function isExposuresAvailable(siteContext) {
    return !!(siteContext && siteContext.exposures);
}

/**
 * Whether a line-chart metric option is available for the current site.
 * @param {string} metric
 * @param {WidgetSiteContext} siteContext
 */
export function isMetricAvailable(metric, siteContext) {
    if (metric === 'time_saved') return isTimeSaveAvailable(siteContext);
    return true;
}

/**
 * Whether a kpi-bar KPI option is available for the current site.
 * @param {string} kpi
 * @param {WidgetSiteContext} siteContext
 */
export function isKpiAvailable(kpi, siteContext) {
    if (kpi === 'time_saved') return isTimeSaveAvailable(siteContext);
    if (kpi === 'exposures') return isExposuresAvailable(siteContext);
    return true;
}

/**
 * Whether a persisted layout item should be shown for the current site.
 * Hidden items remain in the saved layout for when the user switches sites.
 * @param {{ widgetType: string; config?: Object }} item
 * @param {WidgetSiteContext} siteContext
 */
export function isLayoutItemAvailable(item, siteContext) {
    if (!item || !item.widgetType) return false;
    const config = item.config || {};
    if (item.widgetType === 'line-chart') {
        return isMetricAvailable(config.metric, siteContext);
    }
    if (item.widgetType === 'kpi-bar') {
        return isKpiAvailable(config.kpi, siteContext);
    }
    return true;
}

/**
 * @typedef {Object} WidgetStoreContext
 * @property {boolean} [isLoading]
 * @property {Object | null} [overviewData]
 * @property {Object | null} [analyticsData]
 */

/**
 * @param {Array} data
 */
function isChartSeriesEmpty(data) {
    if (!Array.isArray(data) || !data.length) return true;
    return data.every((value) => value === false || value === null || value === undefined);
}

/**
 * Mirrors DonutsWidget chartData — whether the donut would render chart content
 * instead of the "No data" placeholder (general always renders once charts exist).
 * @param {Object | null | undefined} analyticsData
 * @param {string} donutKey
 */
function resolveDonutsChartData(analyticsData, donutKey) {
    const analyticsChartData = analyticsData && analyticsData.charts;
    if (!analyticsChartData) return null;

    if (donutKey === 'general') {
        return { labels: ['general'] };
    }

    const keyData = analyticsChartData[donutKey];
    if (!keyData || !keyData.data || !Array.isArray(keyData.data) || !keyData.data.length) {
        return null;
    }

    let labels;
    if (keyData.localLabels && keyData.localLabels[0]) labels = keyData.localLabels;
    else if (keyData.labels && keyData.labels[0]) labels = keyData.labels;
    else labels = [];

    if (!labels.length) return null;
    return { labels };
}

/**
 * Whether a widget has loaded data to display (not the "No data" empty state).
 * Returns true while data is still loading so widgets are not hidden prematurely.
 * @param {{ widgetType: string; config?: Object }} item
 * @param {Object} widgetData
 * @param {WidgetStoreContext | null} storeContext
 */
export function hasWidgetData(item, widgetData = {}, storeContext = null) {
    if (!item || !item.widgetType || !storeContext) return true;

    const config = item.config || {};
    const { isLoading, overviewData, analyticsData } = storeContext;

    if (isLoading) return true;

    switch (item.widgetType) {
        case 'line-chart': {
            const byKey = widgetData.chartDataByKey;
            if (byKey === null || byKey === undefined) return true;
            const metric = config.metric || 'avg_score';
            const timeRange = config.timeRange || 'lastMonth';
            const chartItem = byKey[`${metric}:${timeRange}`];
            if (!chartItem) return false;
            if (!chartItem.labels || !chartItem.labels.length) return false;
            const series = chartItem.dataset && chartItem.dataset[0] && chartItem.dataset[0].data;
            return !isChartSeriesEmpty(series);
        }
        case 'room-table': {
            if (widgetData.isLoadingRealTime) return true;
            if (!analyticsData) return true;
            const roomsData = widgetData.roomsData;
            return !!(roomsData && roomsData.length);
        }
        case 'donuts': {
            if (!analyticsData) return true;
            const donut = ['general', 'cleaning', 'maintenance'].includes(config.donut)
                ? config.donut
                : 'general';
            return !!resolveDonutsChartData(analyticsData, donut);
        }
        case 'kpi-bar': {
            if (!overviewData) return true;
            const kpi = config.kpi || 'avg_score';
            const sums = getOverviewSums(overviewData);
            const value = sums[kpi];
            if (value === null || value === undefined || value === '' || value === 0) return false;
            return true;
        }
        default:
            return true;
    }
}

/**
 * Whether a layout item should be rendered: site-available and has data (or still loading).
 * @param {{ widgetType: string; config?: Object }} item
 * @param {WidgetSiteContext} siteContext
 * @param {Object} [widgetData]
 * @param {WidgetStoreContext | null} [storeContext]
 */
export function shouldShowLayoutItem(item, siteContext, widgetData = {}, storeContext = null) {
    return (
        isLayoutItemAvailable(item, siteContext) &&
        hasWidgetData(item, widgetData, storeContext)
    );
}

/**
 * @param {Array} layout
 * @param {WidgetSiteContext} siteContext
 * @param {Object} [widgetData]
 * @param {WidgetStoreContext | null} [storeContext]
 */
export function filterAvailableLayoutItems(layout, siteContext, widgetData = {}, storeContext = null) {
    return (layout || []).filter((item) =>
        shouldShowLayoutItem(item, siteContext, widgetData, storeContext)
    );
}

/**
 * Tall widgets anchored to the right edge (e.g. room-table) keep their slot and
 * block columns for row reflow; they are not repositioned themselves.
 * @param {{ x: Number; y: Number; w: Number; h: Number }} item
 * @param {Number} colNum
 */
function isPinnedSidebar(item, colNum) {
    return item.h >= 3 && item.x + item.w >= colNum;
}

/** @param {Array<[Number, Number]>} ranges */
function mergeColumnRanges(ranges) {
    if (!ranges.length) return [];
    const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
    const merged = [sorted[0]];
    for (let i = 1; i < sorted.length; i++) {
        const last = merged[merged.length - 1];
        const [start, end] = sorted[i];
        if (start <= last[1]) {
            last[1] = Math.max(last[1], end);
        } else {
            merged.push([start, end]);
        }
    }
    return merged;
}

/**
 * Column ranges occupied in row `rowY` by items that are not being reflowed.
 * @param {Array} allItems
 * @param {Number} rowY
 * @param {Set<string>} reflowIds
 */
function getBlockedRangesForRow(allItems, rowY, reflowIds) {
    const ranges = [];
    for (const item of allItems) {
        if (reflowIds.has(item.i)) continue;
        if (item.y <= rowY && rowY < item.y + item.h) {
            ranges.push([item.x, item.x + item.w]);
        }
    }
    return mergeColumnRanges(ranges);
}

/**
 * Widest free segment starting from the left, before the first blocked range.
 * @param {Array<[Number, Number]>} blockedRanges
 * @param {Number} colNum
 */
function getPrimaryReflowSegment(blockedRanges, colNum) {
    if (!blockedRanges.length) {
        return { start: 0, width: colNum };
    }
    let cursor = 0;
    for (const [blockStart, blockEnd] of blockedRanges) {
        if (blockStart > cursor) {
            return { start: cursor, width: blockStart - cursor };
        }
        cursor = Math.max(cursor, blockEnd);
    }
    if (cursor < colNum) {
        return { start: cursor, width: colNum - cursor };
    }
    return { start: 0, width: 0 };
}

/**
 * Evenly distributes row items across `totalWidth`, respecting minW from the registry.
 * @param {Array} rowItems
 * @param {Number} startX
 * @param {Number} totalWidth
 */
function distributeRowItems(rowItems, startX, totalWidth) {
    if (!rowItems.length || totalWidth <= 0) return;

    rowItems.sort((a, b) => a.x - b.x);
    const count = rowItems.length;
    const baseW = Math.floor(totalWidth / count);
    let remainder = totalWidth % count;
    let x = startX;

    rowItems.forEach((item) => {
        const definition = getWidgetDefinition(item.widgetType);
        const minW = definition ? definition.minW : 1;
        let w = baseW + (remainder > 0 ? 1 : 0);
        if (remainder > 0) remainder -= 1;
        w = Math.max(w, minW);
        item.x = x;
        item.w = w;
        x += w;
    });
}

/**
 * Repacks filtered layout items so each row fills available horizontal space.
 * Pinned right-edge sidebars (room table) stay fixed and reserve their columns.
 * Operates on a copy — the persisted layout in the store is never mutated.
 *
 * Example: 4 line charts (w=3 at x=0,3,6,9) with one hidden → 3 charts at w=4, x=0,4,8
 * within the 12 columns left of a w=4 sidebar at x=12.
 *
 * @param {Array} layout filtered, available items only
 * @param {Number} [colNum]
 * @returns {Array}
 */
export function reflowLayoutItems(layout, colNum = GRID_COL_NUM) {
    if (!layout || !layout.length) return [];

    const items = layout.map((item) => ({ ...item }));
    const pinnedIds = new Set(
        items.filter((item) => isPinnedSidebar(item, colNum)).map((item) => item.i)
    );

    const rowYs = [
        ...new Set(items.filter((item) => !pinnedIds.has(item.i)).map((item) => item.y))
    ].sort((a, b) => a - b);

    rowYs.forEach((rowY) => {
        const rowItems = items.filter((item) => item.y === rowY && !pinnedIds.has(item.i));
        if (!rowItems.length) return;

        const reflowIds = new Set(rowItems.map((item) => item.i));
        const blocked = getBlockedRangesForRow(items, rowY, reflowIds);
        const segment = getPrimaryReflowSegment(blocked, colNum);
        if (segment.width <= 0) return;

        distributeRowItems(rowItems, segment.start, segment.width);
    });

    return items;
}

/**
 * Config schema with metric/kpi options filtered to those available for the site.
 * @param {{ configSchema?: Array }} definition
 * @param {WidgetSiteContext} siteContext
 */
export function getFilteredConfigSchema(definition, siteContext) {
    if (!definition || !definition.configSchema) return [];
    return definition.configSchema.map((field) => {
        if (field.key === 'metric') {
            return {
                ...field,
                options: field.options.filter((option) => isMetricAvailable(option.value, siteContext))
            };
        }
        if (field.key === 'kpi') {
            return {
                ...field,
                options: field.options.filter((option) => isKpiAvailable(option.value, siteContext))
            };
        }
        return field;
    });
}

/**
 * Default layout replicating the classic Overview arrangement on a 16-column grid:
 * - Row of last-month metric line charts (4 charts, columns 0-11)
 * - Row of last-year metric line charts below it
 * - Room table as a full-height column on the right (columns 12-15)
 * - Three analytics donuts along the bottom
 * Total height is GRID_ROW_COUNT (6) row units, matching the old 75vh / 6-row CSS grid.
 * @returns {Array<{ i: String; x: Number; y: Number; w: Number; h: Number; widgetType: String; config: Object; }>}
 */
export function buildDefaultLayout(siteContext = {}) {
    const layout = [];
    const metrics = ['avg_score', 'complaints', 'response_time_SLA', 'time_saved'].filter((metric) =>
        isMetricAvailable(metric, siteContext)
    );

    metrics.forEach((metric, index) => {
        layout.push({
            i: `line-${metric}-lastMonth`,
            x: index * 3,
            y: 0,
            w: 3,
            h: 2,
            widgetType: 'line-chart',
            config: { metric, timeRange: 'lastMonth' }
        });
    });

    metrics.forEach((metric, index) => {
        layout.push({
            i: `line-${metric}-lastYear`,
            x: index * 3,
            y: 2,
            w: 3,
            h: 2,
            widgetType: 'line-chart',
            config: { metric, timeRange: 'lastYear' }
        });
    });

    layout.push({
        i: 'room-table',
        x: 12,
        y: 0,
        w: 4,
        h: 6,
        widgetType: 'room-table',
        config: { displayMode: 'table' }
    });

    const donuts = ['general', 'cleaning', 'maintenance'];
    donuts.forEach((donut, index) => {
        layout.push({
            i: `donut-${donut}`,
            x: index * 4,
            y: 4,
            w: 4,
            h: 2,
            widgetType: 'donuts',
            config: { donut }
        });
    });

    // Distribute row widths (e.g. 3 metrics → w=4 each) so the stored default
    // already fills the grid before the first render.
    return reflowLayoutItems(layout);
}

/**
 * Keeps only layout items that match the persisted contract and a known widget type.
 * @param {Array} layout
 * @returns {Array | null} normalized layout, or null when nothing valid remains
 */
export function sanitizeLayout(layout) {
    if (!Array.isArray(layout)) return null;
    const items = layout.filter(
        (item) =>
            item &&
            typeof item.i === 'string' &&
            typeof item.widgetType === 'string' &&
            widgetRegistry[item.widgetType] &&
            [item.x, item.y, item.w, item.h].every((n) => typeof n === 'number' && isFinite(n))
    );
    if (!items.length) return null;
    return items.map((item) => ({
        i: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
        widgetType: item.widgetType,
        config: item.config && typeof item.config === 'object' ? { ...item.config } : {}
    }));
}

/**
 * Mirrors item x positions so the grid visually flips for RTL locales
 * (vue-grid-layout has no native RTL support). The function is its own inverse.
 * @param {Array} layout
 * @param {Number} [colNum]
 */
export function mirrorLayoutForRtl(layout, colNum = GRID_COL_NUM) {
    return (layout || []).map((item) => ({
        ...item,
        x: Math.max(0, colNum - item.x - item.w)
    }));
}

/**
 * Single-column arrangement of the given layout (used for small screens),
 * ordered by the item's visual position (top to bottom, then start to end).
 * @param {Array} layout
 */
export function buildStackedLayout(layout) {
    const sorted = [...(layout || [])].sort((a, b) => a.y - b.y || a.x - b.x);
    let y = 0;
    return sorted.map((item) => {
        const stacked = { ...item, x: 0, y, w: 1 };
        y += item.h;
        return stacked;
    });
}

export default widgetRegistry;
